import type { ClickHouseClient, ClickHouseClientConfigOptions } from '@clickhouse/client'

import { createClient } from '@clickhouse/client'

import type { DaemonClientOptions } from './types.js'

import { ensureBinary, getInstallPaths } from './install.js'
import { isServerRunning, startServer } from './server.js'

export type { ClickHouseClient }

/**
 * Creates a native Node.js ClickHouse client
 */
export function createClickHouseClient(options: {
  database?: string
  password?: string
  port?: number
  url?: string
  username?: string
}): ClickHouseClient {
  const port = options.port || 8123
  const url = options.url || `http://localhost:${port}`

  const clientOptions: ClickHouseClientConfigOptions = { url }

  if (options.database) {clientOptions.database = options.database}
  if (options.username) {clientOptions.username = options.username}
  if (options.password) {clientOptions.password = options.password}

  return createClient(clientOptions)
}

/**
 * Connects to ClickHouse daemon, auto-downloading binary and starting server if needed.
 *
 * @param options - Configuration options
 * @returns Connected ClickHouse client
 */
export async function connect(options: DaemonClientOptions = {}): Promise<ClickHouseClient> {
  const installDir = options.installDir || '~/.clickhouse'
  const database = options.database || 'default'
  const port = options.port || 8123

  // Step 1: Ensure ClickHouse binary is installed
  const binaryPath = await ensureBinary({ installDir })

  // Step 2: Check if server is running, start if needed
  const status = await isServerRunning({ port })

  if (!status.isRunning) {
    const paths = getInstallPaths({ installDir })
    await startServer({
      binaryPath,
      dataDir: paths.dataDir,
      httpPort: port,
      logDir: paths.logDir,
    })
  }

  // Step 3: Create and return client
  return createClickHouseClient({
    database,
    password: options.password,
    port,
    url: options.url,
    username: options.username,
  })
}
